"use client"
import { useMemo, useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Star, SlidersHorizontal } from 'lucide-react'
import { defaultVariantColor } from '../../src/lib/products'
import { titleWithVariant } from '../../src/lib/seo'
import { categorySlugForProduct } from '../../src/lib/products'

type Item = any

const SORTS = [
  { id: 'featured', label: 'Featured' },
  { id: 'priceLow', label: 'Price: Low to High' },
  { id: 'priceHigh', label: 'Price: High to Low' },
  { id: 'rating', label: 'Top Rated' },
  { id: 'reviews', label: 'Most Reviewed' },
]

export default function ProductGridClient({
  items,
  categories,
  initialCategory = 'All',
  initialQuery = '',
  catSlug,
  showCategoryPills = true,
  priorityCount = 0,
}: {
  items: Item[]
  categories: string[]
  initialCategory?: string
  initialQuery?: string
  catSlug?: string
  showCategoryPills?: boolean
  priorityCount?: number
}) {
  const [category, setCategory] = useState<string>(initialCategory)
  const [query, setQuery] = useState<string>(() => initialQuery)
  const [sortBy, setSortBy] = useState<string>('featured')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(false)
  // Progressive rendering to keep mobile hydration light
  const INITIAL_CAP = 12
  const [visibleCount, setVisibleCount] = useState<number>(INITIAL_CAP)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list = items.filter(p => category === 'All' || p.category === category)
    if (q) {
      list = list.filter(p => (
        [p.title, p.category, p.material, ...(p.keywords || [])]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      ))
    }
    const out = [...list]
    switch (sortBy) {
      case 'priceLow': out.sort((a,b)=>(a.salePrice ?? a.price ?? 0) - (b.salePrice ?? b.price ?? 0)); break
      case 'priceHigh': out.sort((a,b)=>(b.salePrice ?? b.price ?? 0) - (a.salePrice ?? a.price ?? 0)); break
      case 'rating': out.sort((a,b)=> (b.rating ?? 0) - (a.rating ?? 0)); break
      case 'reviews': out.sort((a,b)=> (b.reviewsCount ?? 0) - (a.reviewsCount ?? 0)); break
      default: break
    }
    return out
  }, [items, category, query, sortBy])

  // After mount/interaction, reveal the rest during idle time
  // This keeps initial client work small on large lists / mobile devices
  const filteredLen = filtered.length
  useEffect(() => {
    const first = Math.min(INITIAL_CAP, filteredLen)
    setVisibleCount(first)
    const idle = (window as any).requestIdleCallback as undefined | ((cb: any, opts?: any) => any)
    const id = idle ? idle(() => setVisibleCount(filteredLen), { timeout: 1500 }) : setTimeout(() => setVisibleCount(filteredLen), 0)
    return () => { if (!idle) clearTimeout(id as any) }
  }, [filteredLen, category, query, sortBy])

  const allowCategoryChange = !!showCategoryPills
  const hasActive = (allowCategoryChange && category !== 'All') || query.trim() || sortBy !== 'featured'

  return (
    <div className="space-y-5">
      <div className="space-y-4 border bg-white px-4 py-4 shadow-sm">
        {/* Desktop/Tablet: Category pills and inline search+sort */}
        {allowCategoryChange && (
          <div className="hidden sm:flex flex-wrap gap-2">
            {['All', ...categories].map(cat => (
              <button key={cat} type="button" onClick={()=>setCategory(cat)}
                className={`px-3 py-1.5 rounded border text-sm transition ${category===cat?'bg-black text-white border-black shadow-sm':'bg-white hover:bg-gray-50'}`}>{cat}</button>
            ))}
          </div>
        )}
        <div className="hidden sm:flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="relative flex-1">
            <span className="sr-only">Search products</span>
            <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search by title, keyword, material…"
              className="w-full rounded border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/70" />
          </label>
          <label className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-2">
            Sort
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="rounded border px-3 py-2 text-sm bg-white">
              {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
        </div>

        {/* Mobile: Search + filter icon row */}
        <div className="flex items-center gap-3 sm:hidden">
          <label className="relative flex-1">
            <span className="sr-only">Search products</span>
            <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search products…"
              className="w-full rounded border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/70" />
          </label>
          <button
            type="button"
            aria-expanded={mobileFiltersOpen}
            aria-controls="mobile-filters"
            onClick={()=>setMobileFiltersOpen(v=>!v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded border bg-white hover:bg-gray-50"
            aria-label={mobileFiltersOpen ? 'Hide filters' : 'Show filters'}
          >
            <SlidersHorizontal size={18} />
          </button>
        </div>

        {/* Mobile: Filter panel (Sort + Product Type) */}
        {mobileFiltersOpen && (
          <div id="mobile-filters" className="sm:hidden space-y-4 border bg-white p-3">
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-gray-500">Sort by</div>
              <div role="listbox" aria-label="Sort by" className="rounded border overflow-hidden divide-y divide-gray-200">
                {SORTS.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    role="option"
                    aria-selected={sortBy===s.id}
                    onClick={()=>setSortBy(s.id)}
                    className={`w-full text-left px-3 py-2 text-sm ${sortBy===s.id ? 'bg-black text-white' : 'bg-white hover:bg-gray-50'}`}
                  >{s.label}</button>
                ))}
              </div>
            </div>
            {allowCategoryChange && (
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Product type</div>
                <div className="flex flex-wrap gap-2">
                  {['All', ...categories].map(cat => (
                    <button key={cat} type="button" onClick={()=>setCategory(cat)}
                      className={`px-3 py-1.5 rounded border text-sm transition ${category===cat?'bg-black text-white border-black shadow-sm':'bg-white hover:bg-gray-50'}`}
                      aria-pressed={category===cat}
                    >{cat}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button type="button" onClick={()=>{setCategory('All'); setSortBy('featured')}} className="text-sm underline decoration-dotted">Reset</button>
            <button type="button" onClick={()=>setMobileFiltersOpen(false)} className="inline-flex items-center rounded border px-3 py-1.5 text-sm hover:bg-gray-50">Done</button>
            </div>
          </div>
        )}
        {hasActive && (
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
            <span>{filtered.length} {filtered.length===1?'item':'items'} found</span>
            <div className="flex flex-wrap gap-2 text-xs">
              {allowCategoryChange && category!=='All' && <span className="bg-gray-100 px-2 py-0.5 text-gray-600">Category: {category}</span>}
              {query.trim()!=='' && <span className="bg-gray-100 px-2 py-0.5 text-gray-600">Search: “{query.trim()}”</span>}
              {sortBy!=='featured' && <span className="bg-gray-100 px-2 py-0.5 text-gray-600">{SORTS.find(s=>s.id===sortBy)?.label}</span>}
              <button type="button" onClick={()=>{setCategory('All');setQuery('');setSortBy('featured')}} className="underline decoration-dotted">Reset</button>
            </div>
          </div>
        )}
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.slice(0, visibleCount).map((p:any, i:number) => {
          const href = catSlug
            ? `/category/${catSlug}/${p.slug}/`
            : `/category/${categorySlugForProduct(p)}/${p.slug}/`
          const showStrike = typeof p.salePrice==='number' && typeof p.listPrice==='number' && p.salePrice < p.listPrice
          return (
            <div key={p.id} className="border bg-white shadow-sm hover:shadow-md transition">
              <Link href={href} className="block overflow-hidden">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <Image
                    src={p.cover}
                    alt={p.title}
                    fill
                    className="object-cover"
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    priority={priorityCount > 0 && i < priorityCount}
                  />
                </div>
              </Link>
              <div className="p-4 space-y-2">
                <a href={href} className="font-semibold leading-snug text-sm md:text-base line-clamp-2 hover:underline block">{p.title}</a>
                {typeof p.rating === 'number' && (
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <span className="inline-flex items-center gap-0.5" aria-label={`Rated ${p.rating} out of 5`}>
                      {Array.from({ length: 5 }).map((_, idx) => {
                        const fill = Math.max(0, Math.min(1, p.rating - idx))
                        const fillPct = Math.round(fill * 100)
                        return (
                          <span key={idx} className="relative inline-block h-3 w-3 text-gray-300">
                            <Star size={12} className="h-3 w-3 text-gray-300" />
                            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                              <Star size={12} className="h-3 w-3 fill-current text-amber-500" />
                            </span>
                          </span>
                        )
                      })}
                    </span>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-gray-600 truncate">{p.category}</div>
                    <div className="text-base font-semibold flex items-baseline gap-2">
                      <span>₹{((p.salePrice ?? p.price) || 0).toLocaleString('en-IN')}</span>
                      {showStrike && (
                        <span className="text-xs font-normal text-gray-500 line-through">₹{(p.listPrice||0).toLocaleString('en-IN')}</span>
                      )}
                    </div>
                  </div>
                  {(() => {
                    const hrefBuy = (p as any)?.amazonUrl || (p as any)?.amazon?.url || null
                    if (hrefBuy) {
                      return (
                        <a href={hrefBuy} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded border border-brand bg-white px-3 py-2 text-sm font-medium text-black transition-colors duration-200 hover:bg-brand hover:text-white whitespace-nowrap">
                          Buy on Amazon
                          <img src="/assets/brand/icons/amazon.svg" alt="Amazon" className="h-4 w-4" loading="lazy" decoding="async" width="16" height="16" />
                        </a>
                      )
                    }
                    return (
                      <button type="button" onClick={() => window.dispatchEvent(new Event('open-coming-soon'))} className="inline-flex items-center justify-center rounded border px-3 py-2 text-sm font-medium hover:bg-gray-50 whitespace-nowrap">
                        Coming soon
                      </button>
                    )
                  })()}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
